import axios from '@/api/axios';
import {toQueryString} from '@/api/queryString';
import type {
  AdminPaginatedEnvelope,
  AfriexOTCRate,
  AfriexOTCTrade,
  AfriexQueryParams,
  AfriexRate,
  AfriexRates,
  UserType,
} from '@/types';
import {parseBlobErrorBody} from '@/utils/downloadFile';

export const ratesService = {
  getAppRatesList: async (
    target: UserType = 'AFRIEX',
  ): Promise<AfriexRate[]> => {
    const url = `/v2/admin/rates?target=${target}`;
    const response = await axios.get<AfriexRate[]>(url);
    return response?.data;
  },
  getOTCRatesList: async (): Promise<AfriexOTCRate[]> => {
    const url = `/v2/admin/rates`;
    const response = await axios.get<AfriexOTCRate[]>(url);
    return response?.data;
  },

  // The rates-upload screen assembles this body from a spreadsheet (app rates,
  // OTC rates, tolerance, arbitrage report, target), so the service just
  // forwards whatever the screen built rather than restating that shape.
  updateRates: async (
    payload: Record<string, unknown>,
  ): Promise<AfriexRates> => {
    const url = `/v2/admin/rates`;
    const response = await axios.post<AfriexRates>(url, payload);
    return response?.data;
  },
  getOTCTradeList: async (
    filters: AfriexQueryParams,
  ): Promise<AdminPaginatedEnvelope<AfriexOTCTrade[]>> => {
    const params = {page: 0, limit: 10, ...filters};
    const url = `/v2/admin/otc-volume?${toQueryString(params)}`;

    const response =
      await axios.get<AdminPaginatedEnvelope<AfriexOTCTrade[]>>(url);
    return response?.data;
  },
  // The whole filtered list as a CSV, streamed by the server. Only the
  // filters are sent: the server ignores paging for a download.
  downloadOTCTradeList: async (filters: AfriexQueryParams): Promise<Blob> => {
    const url = `/v2/admin/otc-volume?${toQueryString({...filters, action: 'DOWNLOAD'})}`;
    try {
      const response = await axios.get<Blob>(url, {responseType: 'blob'});
      return response.data;
    } catch (error) {
      throw await parseBlobErrorBody(error);
    }
  },
  createOTCTrade: async (payload: {
    otcVolume: AfriexOTCTrade;
    otcRates: AfriexOTCRate[];
  }): Promise<AfriexOTCTrade> => {
    const url = `/v2/admin/otc-volume`;
    const response = await axios.post<AfriexOTCTrade>(url, payload);
    return response?.data;
  },
  updateOTCTrade: async (
    trades: Partial<AfriexOTCTrade>[],
  ): Promise<AfriexOTCTrade> => {
    const url = `/v2/admin/otc-volume`;
    const response = await axios.patch<AfriexOTCTrade>(url, trades);
    return response?.data;
  },
  approveOTCTrade: async (rateId: string): Promise<AfriexOTCTrade> => {
    const url = `/v2/admin/otc-volume/${rateId}`;
    const response = await axios.patch<AfriexOTCTrade>(url, {});
    return response?.data;
  },
};
